<template>
  <div v-if="model" class="asc-dialog--container">
    <transition name="asc-dialog-fade">
      <div class="asc-dialog-mask">
        <div
          :style="{
            width: widthIsPercent ? width + '%' : width + 'px',
          }"
          :class="[
            isSimpleDialog ? 'asc-dialog--simple' : 'asc-dialog--complex',
            'asc-dialog',
          ]"
        >
          <v-card>
            <v-card-title>
              <asc-dialog-header
                :value="text"
                :on-close="handleClose"
                :show-title="showTitle"
                :show-close-button="isOnCloseEventFound"
              ></asc-dialog-header>
            </v-card-title>
            <v-card-text>
              <div class="asc-dialog-slot">
                <slot></slot>
              </div>
            </v-card-text>
            <template v-if="getButtonsCount">
              <v-divider
                v-if="!isSimpleDialog || showActionsBorder"
              ></v-divider>
              <v-card-actions>
                <v-row
                  justify="end"
                  no-gutters
                  :class="{
                    'flex-shrink-0': !isSimpleDialog,
                  }"
                >
                  <div
                    v-if="isSecondaryEventFound"
                    :class="{
                      'mr-1': getButtonsCount === 2,
                      'col cols-6': isSimpleDialog && getButtonsCount === 2,
                      col: isSimpleDialog && getButtonsCount === 1,
                    }"
                  >
                    <asc-tooltip
                      :tooltip="secondaryButtonTooltip"
                      v-bind="getSecondaryTooltipPosition"
                      :tooltip-length="0"
                    >
                      <asc-accent-button
                        :block="isSimpleDialog"
                        :disabled="!secondaryAvailable"
                        :loading="loading"
                        :on-click="onSecondary"
                        :button-text="secondaryButtonText"
                        secondary
                      ></asc-accent-button>
                    </asc-tooltip>
                  </div>
                  <div
                    v-if="isPrimaryEventFound"
                    :class="{
                      'ml-1': getButtonsCount === 2,
                      'col cols-6': isSimpleDialog && getButtonsCount === 2,
                      col: isSimpleDialog && getButtonsCount === 1,
                    }"
                  >
                    <asc-tooltip
                      :tooltip="primaryButtonTooltip"
                      v-bind="getPrimaryTooltipPosition"
                      :tooltip-length="0"
                    >
                      <asc-accent-button
                        :block="isSimpleDialog"
                        :disabled="!primaryAvailable"
                        :loading="loading"
                        :on-click="onPrimary"
                        :button-text="primaryButtonText"
                      ></asc-accent-button>
                    </asc-tooltip>
                  </div>
                </v-row>
              </v-card-actions>
            </template>
          </v-card>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import AscAccentButton from './AscAccentButton'
import { AscDialogMixin } from '@/components/mixins'
import AscDialogHeader from './AscDialogHeader'
import AscTooltip from '@/components/atoms/AscTooltip'

export default {
  name: 'AscDialog',

  components: {
    AscAccentButton,
    AscDialogHeader,
    AscTooltip,
  },

  mixins: [AscDialogMixin],

  props: {
    text: {
      type: String,
      default: '',
    },
    width: {
      type: Number,
      default: 340,
    },
    widthIsPercent: {
      type: Boolean,
      default: false,
    },
    fullWidthButtons: {
      type: Boolean,
      default: false,
    },
    primaryButtonText: {
      type: String,
      default: 'OK',
    },
    secondaryButtonText: {
      type: String,
      default: 'Label',
    },
    primaryAvailable: {
      type: Boolean,
      default: true,
    },
    secondaryAvailable: {
      type: Boolean,
      default: true,
    },
    primaryButtonTooltip: {
      type: String,
      default: '',
    },
    secondaryButtonTooltip: {
      type: String,
      default: '',
    },
    primaryButtonTooltipPosition: {
      type: String,
      default: 'left',
      validator: prop => ['top', 'bottom', 'left', 'right'].includes(prop),
    },
    secondaryButtonTooltipPosition: {
      type: String,
      default: 'left',
      validator: prop => ['top', 'bottom', 'left', 'right'].includes(prop),
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    onPrimary: {
      type: Function,
      default: undefined,
    },
    onSecondary: {
      type: Function,
      default: undefined,
    },
    showActionsBorder: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    getButtonsCount() {
      return this.isPrimaryEventFound && this.isSecondaryEventFound
        ? 2
        : this.isPrimaryEventFound || this.isSecondaryEventFound
        ? 1
        : 0
    },
    isSimpleDialog() {
      return this.width <= 340 || this.fullWidthButtons
    },
    isPrimaryEventFound() {
      return this.onPrimary
    },
    isSecondaryEventFound() {
      return this.onSecondary
    },
    isOnCloseEventFound() {
      if (this.onClose) {
        return true
      }
      return false
    },
    getPrimaryTooltipPosition() {
      return {
        [this.primaryButtonTooltipPosition]: true,
      }
    },
    getSecondaryTooltipPosition() {
      return {
        [this.secondaryButtonTooltipPosition]: true,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.asc-dialog-mask {
  z-index: 9998;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;

  .asc-dialog {
    background: $primary-white;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    min-width: 340px;
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 100px);

    .v-card {
      &__title {
        color: $gray-1000 !important;
        padding: 15px 16px;
      }

      &__text {
        padding-bottom: 0;
        padding-top: 0;
        min-height: 124.34px;
        color: $primary-black;
        font-size: $font-size-md;
      }

      &__actions {
        min-height: 64px;
        padding: 16px;
        flex-direction: row-reverse;
      }

      .v-divider {
        border-color: $gray-300;
      }
    }

    &--simple {
      .v-card {
        &__actions {
          padding: 16px 16px 16px;
          flex-direction: row;
        }
      }
    }
  }
}

.asc-modal-fade-enter,
.asc-modal-fade-leave-active {
  opacity: 0;
}

.asc-modal-fade-enter-active,
.asc-modal-fade-leave-active {
  transition: opacity 0.5s ease;
}

.asc-dialog-slot {
  overflow-x: auto;
  max-height: calc(100vh - 160px);
}
</style>
